import { useEffect, useRef, useState, useMemo } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import canaisComercializacaoValidationSchema from './validation';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { ROUTES } from 'routes/constants';

export enum CuffaCommercializationChannelEnum {
  COOPERATIVAS_AGRICULTURA_FAMILIAR = 'COOPERATIVAS DA AGRICULTURA FAMILIAR',
  AGROINDUSTRIAS_AGRICULTURA_FAMILIAR = 'AGROINDÚSTRIAS DA AGRICULTURA FAMILIAR',
  COOPERATIVAS_NAO_AGRICULTURA_FAMILIAR = 'COOPERATIVAS NÃO ENQUADRADAS COMO DE AGRICULTURA FAMILIAR',
  AGROINDUSTRIAS_NAO_AGRICULTURA_FAMILIAR = 'AGROINDÚSTRIAS OU INDÚSTRIAS NÃO ENQUADRADAS COMO DE AGRICULTURA FAMILIAR',
  BARES_RESTAURANTES_LANCHONETES_HOTEIS = 'BARES, RESTAURANTES, LANCHONETES E HOTÉIS',
  EXPORTACAO_EMPRESAS_EXPORTADORAS = 'EXPORTAÇÃO OU EMPRESAS EXPORTADORAS (COMERCIALIZAÇÃO PARA OUTROS PAÍSES)',
  FEIRAS = 'FEIRAS',
  MERCADOS_SUPERMERCADOS_OUTROS_ESTADOS = 'MERCADOS/ SUPERMERCADOS DE OUTROS ESTADOS',
  MERCADOS_SUPERMERCADOS_OUTROS_MUNICIPIOS_MT = 'MERCADOS/ SUPERMERCADOS DE OUTROS MUNICÍPIOS DE MATO GROSSO',
  MERCADOS_SUPERMERCADOS_QUITANDAS_MUNICIPIO = 'MERCADOS/ SUPERMERCADOS/QUITANDAS DO MUNICÍPIO',
  PROGRAMA_AQUISICAO_ALIMENTOS_PAA = 'PROGRAMA DE AQUISIÇÃO DE ALIMENTOS - PAA',
  PROGRAMA_NACIONAL_ALIMENTACAO_ESCOLAR_PNAE = 'PROGRAMA NACIONAL DE ALIMENTAÇÃO ESCOLAR - PNAE',
  REVENDEDORES_ATRAVESSADORES_INTERMEDIARIOS = 'REVENDEDORES/ ATRAVESSADORES/ INTERMEDIÁRIOS',
  VENDA_DIRETA_CONSUMIDOR_FINAL = 'VENDA DIRETA PARA O CONSUMIDOR FINAL',
  VENDAS_ORGAOS_PUBLICOS_COMPRAS_GOVERNAMENTAIS = 'VENDAS PARA ÓRGÃOS PÚBLICOS EM GERAL (COMPRAS GOVERNAMENTAIS)',
  OUTRO = 'OUTRO',
}

const channelToBackendKeyMap: { [key in CuffaCommercializationChannelEnum]?: string } = {
  [CuffaCommercializationChannelEnum.COOPERATIVAS_AGRICULTURA_FAMILIAR]: 'cooperativas_agricultura_familiar',
  [CuffaCommercializationChannelEnum.AGROINDUSTRIAS_AGRICULTURA_FAMILIAR]: 'agroindustrias_agricultura_familiar',
  [CuffaCommercializationChannelEnum.COOPERATIVAS_NAO_AGRICULTURA_FAMILIAR]: 'cooperativas_nao_agricultura_familiar',
  [CuffaCommercializationChannelEnum.AGROINDUSTRIAS_NAO_AGRICULTURA_FAMILIAR]: 'agroindustrias_nao_agricultura_familiar',
  [CuffaCommercializationChannelEnum.BARES_RESTAURANTES_LANCHONETES_HOTEIS]: 'bares_restaurantes_hoteis',
  [CuffaCommercializationChannelEnum.EXPORTACAO_EMPRESAS_EXPORTADORAS]: 'exportacao_empresas_exportadoras',
  [CuffaCommercializationChannelEnum.FEIRAS]: 'feiras',
  [CuffaCommercializationChannelEnum.MERCADOS_SUPERMERCADOS_OUTROS_ESTADOS]: 'mercados_outros_estados',
  [CuffaCommercializationChannelEnum.MERCADOS_SUPERMERCADOS_OUTROS_MUNICIPIOS_MT]: 'mercados_outros_municipios_mt',
  [CuffaCommercializationChannelEnum.MERCADOS_SUPERMERCADOS_QUITANDAS_MUNICIPIO]: 'mercados_municipio',
  [CuffaCommercializationChannelEnum.PROGRAMA_AQUISICAO_ALIMENTOS_PAA]: 'programa_aquisicao_alimentos',
  [CuffaCommercializationChannelEnum.PROGRAMA_NACIONAL_ALIMENTACAO_ESCOLAR_PNAE]: 'programa_alimentacao_escolar',
  [CuffaCommercializationChannelEnum.REVENDEDORES_ATRAVESSADORES_INTERMEDIARIOS]: 'revendedores_atravessadores',
  [CuffaCommercializationChannelEnum.VENDA_DIRETA_CONSUMIDOR_FINAL]: 'venda_direta_consumidor',
  [CuffaCommercializationChannelEnum.VENDAS_ORGAOS_PUBLICOS_COMPRAS_GOVERNAMENTAIS]: 'vendas_orgaos_publicos',
};

const backendKeyToChannelMap = Object.fromEntries(
  Object.entries(channelToBackendKeyMap).map(([key, value]) => [value, key])
) as { [key: string]: CuffaCommercializationChannelEnum };
backendKeyToChannelMap['outros'] = CuffaCommercializationChannelEnum.OUTRO;

const canaisOptions = Object.values(CuffaCommercializationChannelEnum);

export function buildPayloadFromValues(values: { ordens: { value: string; order: number }[]; outroTexto?: string }) {
  const payload: { [key: string]: any } = {};
  values.ordens.forEach(ordem => {
    const channelName = ordem.value as CuffaCommercializationChannelEnum;
    if (channelName === CuffaCommercializationChannelEnum.OUTRO) {
      payload.outros = payload.outros || [];
      payload.outros.push({ priority: ordem.order, description: values.outroTexto ?? '' });
    } else {
      const backendKey = channelToBackendKeyMap[channelName];
      if (backendKey) {
        payload[backendKey] = ordem.order;
      }
    }
  });
  return payload;
}

export function parseSavedData(savedData: any) {
  const canaisSelecionados: string[] = [];
  const ordens: { value: string; order: number }[] = [];
  let outroTexto = '';

  for (const [backendKey, data] of Object.entries(savedData || {})) {
    const frontendValue = backendKeyToChannelMap[backendKey];
    if (!frontendValue) continue;

    let priority: number | undefined;

    if (frontendValue === CuffaCommercializationChannelEnum.OUTRO && Array.isArray(data) && data.length > 0) {
      priority = data[0]?.priority;
      outroTexto = data[0]?.description ?? '';
    } else if (typeof data === 'number') {
      priority = data;
    }

    if (priority) {
      canaisSelecionados.push(frontendValue);
      ordens.push({ value: frontendValue, order: priority });
    }
  }

  ordens.sort((a, b) => a.order - b.order);
  return { canaisSelecionados, ordens, outroTexto };
}
export function useCanaisComercializacao() {
  const navigate = useNavigate();
  const { salvarDadosParciais } = useCuffaStore();
  const { uuid } = useParams<{ uuid: string }>();

  const [initialLoading, setInitialLoading] = useState(true);
  const advanceNext = useRef(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const formik = useFormik({
    initialValues: {
      canaisSelecionados: [] as string[],
      ordens: [] as { value: string; order: number }[],
      outroTexto: '',
    },
    validationSchema: canaisComercializacaoValidationSchema,
    onSubmit: async (values) => {
      if (!uuid) {
        setSnackbar({ open: true, message: 'UUID da coleta não encontrado.', severity: 'error' });
        return;
      }

      const ordensDefinidas = values.ordens.filter(o => o.order > 0);
      if (ordensDefinidas.length !== values.canaisSelecionados.length) {
        setSnackbar({ open: true, message: 'Defina uma prioridade para cada canal selecionado.', severity: 'error' });
        return;
      }
      if (new Set(ordensDefinidas.map((o) => o.order)).size !== values.canaisSelecionados.length) {
        setSnackbar({ open: true, message: 'As prioridades devem ser únicas para cada canal.', severity: 'error' });
        return;
      }

      setSaving(true);
      const payload: { [key: string]: any } = {};
      values.ordens.forEach(ordem => {
        const channelName = ordem.value as CuffaCommercializationChannelEnum;
        if (channelName === CuffaCommercializationChannelEnum.OUTRO) {
          payload.outros = [{ priority: ordem.order, description: values.outroTexto }];
        } else {
          const backendKey = channelToBackendKeyMap[channelName];
          if (backendKey) {
            payload[backendKey] = ordem.order;
          }
        }
      });
      try {
        await CuffaAPI.updateCommercializationChannels(uuid, payload);
        salvarDadosParciais({ canaisComercializacao: values });
        setSnackbar({ open: true, message: 'Dados salvos com sucesso!', severity: 'success' });
        if (advanceNext.current) {
          const nextRoute = ROUTES.CREDITO_FINANCIAMENTO_CUFFA_INCLUIR.replace(':uuid', uuid);
          navigate(nextRoute);
        }
      } catch (error: any) {
        setSnackbar({ open: true, message: error.message ?? 'Erro ao salvar os dados.', severity: 'error' });
      } finally {
        setSaving(false);
        advanceNext.current = false;
      }
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!uuid) {
        setSnackbar({ open: true, message: 'UUID da coleta não encontrado.', severity: 'error' });
        setInitialLoading(false);
        return;
      }

      // Primeiro, tentar restaurar do localStorage (chave específica do uuid, depois chave global)
      try {
        const storageKeys: string[] = [];
        if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
        storageKeys.push('cuffa-storage');

        for (const storageKey of storageKeys) {
          try {
            const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.canaisComercializacao;
            if (!saved) continue;

            const canaisSelecionados: string[] = Array.isArray(saved.canaisSelecionados) ? saved.canaisSelecionados : [];
            const ordens: { value: string; order: number }[] = Array.isArray(saved.ordens) ? saved.ordens : [];
            const outroTexto: string = typeof saved.outroTexto === 'string' ? saved.outroTexto : '';

            formik.setValues({ canaisSelecionados, ordens, outroTexto });
            setInitialLoading(false);
            return;
          } catch (e) {
            console.warn(`Falha ao parsear ${storageKey} ao restaurar canaisComercializacao:`, e);
            continue;
          }
        }
      } catch (e) {
        console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar canaisComercializacao', e);
      }

      // Se não houver dados no storage, buscar pela API
      try {
        const response = await CuffaAPI.getDataByID(uuid);
        const savedData = response.dados?.commercialization_channels;

        if (savedData && Object.keys(savedData).length > 0) {
          const canaisSelecionados: string[] = [];
          const ordens: { value: string; order: number }[] = [];
          let outroTexto = '';

          for (const [backendKey, data] of Object.entries(savedData)) {
            const frontendValue = backendKeyToChannelMap[backendKey];
            if (!frontendValue) continue;

            let priority: number | undefined;

            if (frontendValue === CuffaCommercializationChannelEnum.OUTRO && Array.isArray(data) && data.length > 0) {
              priority = data[0]?.priority;
              outroTexto = data[0]?.description ?? '';
            } else if (typeof data === 'number') {
              priority = data;
            }

            if (priority) {
              canaisSelecionados.push(frontendValue);
              ordens.push({ value: frontendValue, order: priority });
            }
          }

          ordens.sort((a, b) => a.order - b.order);
          formik.setValues({ canaisSelecionados, ordens, outroTexto });
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        setSnackbar({ open: true, message: 'Erro ao carregar os dados. Tente novamente.', severity: 'error' });
      } finally {
        setInitialLoading(false);
      }
    };
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  type Ordem = { value: string; order: number };

  const handleSelecionar = (nome: string) => {
    const { canaisSelecionados, ordens } = formik.values as {
      canaisSelecionados: string[];
      ordens: Ordem[];
    };

    const isChecked = canaisSelecionados.includes(nome);

    let novosCanais: string[];
    let novasOrdens: Ordem[];

    if (isChecked) {
      novosCanais = canaisSelecionados.filter((c) => c !== nome);
      const ordensRestantes: Ordem[] = ordens.filter((o: Ordem) => o.value !== nome);
      const ordensOrdenadas: Ordem[] = [...ordensRestantes].sort((a: Ordem, b: Ordem) => a.order - b.order);

      novasOrdens = ordensOrdenadas.map((ordem: Ordem, index: number) => ({
        ...ordem,
        order: index + 1,
      }));

      if (nome === CuffaCommercializationChannelEnum.OUTRO) {
        formik.setFieldValue('outroTexto', '');
      }
    } else {
      novosCanais = [...canaisSelecionados, nome];
      const novaOrdem: Ordem = { value: nome, order: canaisSelecionados.length + 1 };
      novasOrdens = [...ordens, novaOrdem];
    }

    formik.setFieldValue('canaisSelecionados', novosCanais);
    formik.setFieldValue('ordens', novasOrdens);
  };

  const handleOrderChange = (value: string, novaOrdem: number) => {
    const { ordens } = formik.values;
    const ordensCopia = ordens.map(o => ({ ...o }));

    const itemAtualIndex = ordensCopia.findIndex((o) => o.value === value);
    const itemAlvoIndex = ordensCopia.findIndex((o) => o.order === novaOrdem);

    if (itemAlvoIndex > -1) {
      ordensCopia[itemAlvoIndex].order = ordensCopia[itemAtualIndex]?.order ?? 0;
    }
    if (itemAtualIndex > -1) {
      ordensCopia[itemAtualIndex].order = novaOrdem;
    }

    formik.setFieldValue('ordens', ordensCopia);
  };

  const resumoPrioridades = useMemo(() => {
    const selecionados = new Set(formik.values.canaisSelecionados);
    return formik.values.ordens
      .filter(o => selecionados.has(o.value) && o.order > 0)
      .sort((a, b) => a.order - b.order)
      .map(({ value, order }) => {
        if (value === CuffaCommercializationChannelEnum.OUTRO) {
          const desc = formik.values.outroTexto?.trim();
          return { order, label: desc ? `OUTRO - ${desc}` : 'OUTRO' };
        }
        return { order, label: value };
      });
  }, [formik.values.canaisSelecionados, formik.values.ordens, formik.values.outroTexto]);

  const handleSave = async () => {
    advanceNext.current = false;
    await formik.validateForm();
    formik.setTouched({ canaisSelecionados: true, outroTexto: formik.values.canaisSelecionados.includes('OUTRO') });
    
    if (formik.values.canaisSelecionados.length === 0) {
      setSnackbar({ open: true, message: 'Selecione pelo menos um canal.', severity: 'error' });
      return;
    }
    
    formik.handleSubmit();
  };
  
  const handleAdvance = async () => {
    advanceNext.current = true;
    await formik.validateForm();
    formik.setTouched({ canaisSelecionados: true, outroTexto: formik.values.canaisSelecionados.includes('OUTRO') });
    
    if (formik.values.canaisSelecionados.length === 0) {
      setSnackbar({ open: true, message: 'Selecione pelo menos um canal.', severity: 'error' });
      return;
    }
    
    formik.handleSubmit();
  };

  return {
    formik,
    initialLoading,
    advanceNext,
    saving,
    snackbar,
    canaisOptions,
    resumoPrioridades,
    handleSelecionar,
    handleOrderChange,
    handleSave,
    handleAdvance,
    setSnackbar,
  } as const;
}

export default useCanaisComercializacao;
